import React from 'react';
import { Check, X, DotsThreeOutline } from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Compact approval progress bar shown on a leave or clearance card.
 *
 * Props:
 *   steps: [
 *     { key, label_ar, label_en, state: 'done'|'current'|'pending'|'rejected' }
 *   ]
 */
function ApprovalProgress({ steps }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  if (!steps || steps.length === 0) return null;

  const colorByState = {
    done: { dot: 'bg-emerald-500 text-white', line: 'bg-emerald-400', label: 'text-emerald-700' },
    current: { dot: 'bg-[#655dc6] text-white animate-pulse', line: 'bg-[#C4BFF0]', label: 'text-[#655dc6] font-semibold' },
    pending: { dot: 'bg-[#E8E6F5] text-[#9892c8] border border-[#C4BFF0]', line: 'bg-[#E8E6F5]', label: 'text-[#9892c8]' },
    rejected: { dot: 'bg-red-500 text-white', line: 'bg-red-200', label: 'text-red-700 font-semibold' },
    skipped: { dot: 'bg-gray-100 text-gray-400 border border-gray-200', line: 'bg-gray-100', label: 'text-gray-400 line-through' },
  };

  return (
    <div className="flex items-center w-full" data-testid="approval-progress">
      {steps.map((s, i) => {
        const c = colorByState[s.state] || colorByState.pending;
        const label = isAr ? s.label_ar : s.label_en;
        return (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center min-w-[64px]">
              <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] ${c.dot}`} title={label}>
                {s.state === 'done' && <Check size={12} weight="bold" />}
                {s.state === 'rejected' && <X size={12} weight="bold" />}
                {s.state === 'current' && <DotsThreeOutline size={12} weight="fill" />}
                {s.state === 'pending' && <span className="text-[10px]">{i + 1}</span>}
                {s.state === 'skipped' && <span className="text-[10px]">—</span>}
              </div>
              <span className={`text-[10px] mt-0.5 leading-tight text-center max-w-[80px] truncate ${c.label}`}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 -mt-4 ${c.line}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/** Static chain definition per leave_type — must mirror backend
 *  routes/leaves.py:_LEAVE_CHAINS. */
const LEAVE_CHAINS = {
  annual:    ['manager', 'gm', 'admin'],
  unpaid:    ['manager', 'admin'],
  sick:      ['admin'],
};

const ROLE_LABELS = {
  manager: { ar: 'مدير القسم', en: 'Dept. Manager' },
  gm:      { ar: 'المدير العام', en: 'General Manager' },
  admin:   { ar: 'مدير النظام', en: 'System Admin' },
};

const ROLE_TO_PENDING = {
  manager: 'pending_manager',
  gm: 'pending_gm',
  admin: 'pending_admin',
};

/** Build steps array from a leave doc. Renders only the roles that apply
 *  to this leave's chain. */
export function leaveProgressSteps(leave) {
  const status = leave?.status;
  const leaveType = leave?.leave_type || 'annual';
  const chain = leave?.approval_chain && leave.approval_chain.length
    ? leave.approval_chain
    : (LEAVE_CHAINS[leaveType] || LEAVE_CHAINS.annual);
  const managerActed = !!leave?.manager_action;
  const gmActed = !!leave?.gm_action;
  const managerRejected = leave?.manager_action === 'rejected';
  const gmRejected = leave?.gm_action === 'rejected';

  // Helper: how far has the request progressed? Maps current status to
  // the index in `chain` that is currently active (or completed).
  const currentIdx = (() => {
    if (status === 'approved') return chain.length; // past the end = all done
    const target = Object.entries(ROLE_TO_PENDING).find(([, s]) => s === status);
    if (!target) return -1; // 'rejected' or unknown
    const role = target[0];
    return chain.indexOf(role);
  })();

  const steps = [
    { key: 'submitted', label_ar: 'مُقدَّم', label_en: 'Submitted', state: 'done' },
  ];

  chain.forEach((role, idx) => {
    const labels = ROLE_LABELS[role];
    if (!labels) return;
    let state;
    // Check if this specific role acted (rejected at this step)
    const thisRoleRejected = (
      (role === 'manager' && managerRejected) ||
      (role === 'gm' && gmRejected) ||
      (role === 'admin' && status === 'rejected' && !managerRejected && !gmRejected)
    );
    const thisRoleApproved = (
      (role === 'manager' && managerActed && !managerRejected) ||
      (role === 'gm' && gmActed && !gmRejected) ||
      (role === 'admin' && status === 'approved')
    );

    if (thisRoleRejected) {
      state = 'rejected';
    } else if (thisRoleApproved) {
      state = 'done';
    } else if (status === 'rejected') {
      // Rejected upstream — this step never executed
      state = 'pending';
    } else if (currentIdx === -1) {
      state = 'pending';
    } else if (idx < currentIdx) {
      // Earlier roles in the chain that didn't leave a trace = skipped
      state = 'skipped';
    } else if (idx === currentIdx) {
      state = 'current';
    } else {
      state = 'pending';
    }
    steps.push({ key: role, label_ar: labels.ar, label_en: labels.en, state });
  });

  return steps;
}

/** Build steps array from a clearance doc. */
export function clearanceProgressSteps(clearance) {
  const status = clearance?.status;
  const reviewerRole = clearance?.reviewer_role; // 'finance_manager' | 'general_manager' | 'admin' | undefined
  // v1.5.36 — admin-approval was historically stamped inconsistently
  // (only the plain /approve endpoint set `admin_approved_at`; the more
  // powerful /admin-review endpoint didn't). To keep the timeline
  // correct on both new AND legacy records, treat any of the following
  // as evidence that the System-Admin stage has been passed:
  //   • explicit `admin_approved_at` timestamp, OR
  //   • the doc has moved past pending_admin (finance/gm/signing/signed), OR
  //   • `finance_stage_approved` flag set by admin-review.
  const adminApproved = !!(
    clearance?.admin_approved_at
    || clearance?.finance_stage_approved
    || ['pending_finance', 'pending_employee_signature', 'signed'].includes(status)
    || (status === 'rejected' && clearance?.admin_reviewed_at && reviewerRole && reviewerRole !== 'admin')
  );
  // GM path = clearance routed straight from pending_gm (no admin gateway used).
  const gmPath = status === 'pending_gm' || (reviewerRole === 'general_manager' && !adminApproved);

  const steps = [
    { key: 'submitted', label_ar: 'مُقدَّم', label_en: 'Submitted', state: 'done' },
  ];

  if (gmPath) {
    // Finance Manager submitted — single GM step replaces both admin & finance.
    if (status === 'pending_gm') {
      steps.push({ key: 'gm', label_ar: 'المدير العام', label_en: 'General Manager', state: 'current' });
    } else {
      steps.push({ key: 'gm', label_ar: 'المدير العام', label_en: 'General Manager', state: status === 'rejected' ? 'rejected' : 'done' });
    }
  } else {
    // Standard path: Admin gateway → Finance Mgr → Sign
    if (status === 'pending_admin') {
      steps.push({ key: 'admin', label_ar: 'مدير النظام', label_en: 'System Admin', state: 'current' });
    } else if (adminApproved || status === 'pending_finance' || status === 'pending_employee_signature' || status === 'signed') {
      steps.push({ key: 'admin', label_ar: 'مدير النظام', label_en: 'System Admin', state: 'done' });
    } else if (status === 'rejected' && !adminApproved) {
      steps.push({ key: 'admin', label_ar: 'مدير النظام', label_en: 'System Admin', state: 'rejected' });
    } else {
      steps.push({ key: 'admin', label_ar: 'مدير النظام', label_en: 'System Admin', state: 'pending' });
    }

    if (status === 'pending_finance') {
      steps.push({ key: 'finance', label_ar: 'المدير المالي', label_en: 'Finance Manager', state: 'current' });
    } else if (status === 'pending_employee_signature' || status === 'signed') {
      steps.push({ key: 'finance', label_ar: 'المدير المالي', label_en: 'Finance Manager', state: 'done' });
    } else if (status === 'rejected' && adminApproved) {
      steps.push({ key: 'finance', label_ar: 'المدير المالي', label_en: 'Finance Manager', state: 'rejected' });
    } else {
      steps.push({ key: 'finance', label_ar: 'المدير المالي', label_en: 'Finance Manager', state: 'pending' });
    }
  }

  // Final signature step
  if (status === 'signed') {
    steps.push({ key: 'sign', label_ar: 'توقيع الموظف', label_en: 'Employee Signature', state: 'done' });
  } else if (status === 'pending_employee_signature') {
    steps.push({ key: 'sign', label_ar: 'توقيع الموظف', label_en: 'Employee Signature', state: 'current' });
  } else {
    steps.push({ key: 'sign', label_ar: 'توقيع الموظف', label_en: 'Employee Signature', state: 'pending' });
  }

  return steps;
}

export default ApprovalProgress;
